import { useEffect, useRef } from 'react';

import { useToast } from '@rbilabs/universal-components';
import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';

import { ISignUpCompleteQuery, useSignInJwtMutation } from 'generated/graphql-gateway';
import useAuth from 'hooks/auth/use-auth';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useAuthContext } from 'state/auth';
import { useConfirmJwt } from 'state/auth/hooks/use-confirm-jwt';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { SignInPhases } from 'state/mParticle/constants';
import { platform, welcomeEmailDomain } from 'utils/environment';
import { ISignupForm } from 'utils/form/types';
import { encodeBase64String } from 'utils/parse-string';
import { routes } from 'utils/routing';

interface IAutoSignin {
  signUpJwt: string;
  signUpCompleteData?: ISignUpCompleteQuery;
  stopPolling: () => void;
}

const AutoSignin: React.FC<React.PropsWithChildren<IAutoSignin>> = ({
  signUpJwt,
  signUpCompleteData,
  stopPolling,
}) => {
  const { handleConfirmJwt } = useConfirmJwt();
  const { values } = useFormikContext<ISignupForm>();
  const { autoSignInEvent, logRBIEvent } = useMParticleContext();
  const { navigate } = useNavigation();
  const [signInJwtMutation] = useSignInJwtMutation();
  const { formatMessage } = useIntl();
  const {
    modalAuthIsOpen,
    onModalAuthSignInSuccess,
    modalAuthState,
    setModalAuthState,
  } = useAuthContext();
  const toast = useToast();
  const { isAuthenticated } = useAuth();
  const hasMutatedRef = useRef(false);

  const { email } = values;

  useEffect(() => {
    if (
      !hasMutatedRef.current &&
      !isAuthenticated &&
      signUpJwt &&
      signUpCompleteData?.signUpComplete
    ) {
      // TODO: handle a loading state for the sign up form in the campaign auth flow
      stopPolling();
      signInJwtMutation({
        variables: {
          input: {
            email,
            stage: welcomeEmailDomain(),
            platform: platform(),
          },
        },
      })
        .then(async () => {
          hasMutatedRef.current = true;
          const user = encodeBase64String({ value: email });
          logRBIEvent({
            name: CustomEventNames.AUTO_SIGN_IN,
            type: EventTypes.Other,
            attributes: {
              signUpType: email ? 'Email' : 'Phone Number',
            },
          });
          autoSignInEvent({ success: true, phase: SignInPhases.COMPLETE });
          // If the user comes from the cart, the modal auth is not implemented yet. That could happens if the enable-move-signup-after-cart is on.
          // But if the user signup from the modal auth we should show the toast instead
          if (modalAuthIsOpen && user) {
            setModalAuthState({
              shouldDisplaySignInSuccessMessage: false,
            });

            await handleConfirmJwt(signUpJwt, user);
            toast.show({ text: formatMessage({ id: 'autoSignInSuccess' }), variant: 'positive' });
            // If the modal was not opened by the open signin modal then we don't have any signin success callback
            if (!modalAuthState.onSignInSuccess) {
              onModalAuthSignInSuccess();
            } else {
              modalAuthState.onSignInSuccess();
            }
            return;
          }

          navigate(routes.confirmJwt, {
            state: {
              token: signUpJwt,
              user,
              showToast: true,
            },
          });
        })
        .catch(err => {
          throw err;
        });
    }
  }, [
    handleConfirmJwt,
    navigate,
    stopPolling,
    signUpJwt,
    autoSignInEvent,
    signUpCompleteData,
    email,
    signInJwtMutation,
    formatMessage,
    logRBIEvent,
    modalAuthIsOpen,
    isAuthenticated,
    onModalAuthSignInSuccess,
    toast,
    modalAuthState,
    setModalAuthState,
  ]);
  return null;
};

export default AutoSignin;
